import type { ICreateInvitation, IAcceptInvitation } from '@escloud/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { userKeys } from './use-current-user';
import { memberKeys } from './use-members';

import { useApi, useRegion } from '@/context';
import { errorIds, logError } from '@/lib/log-error';
import { getApiUrlForRegion } from '@/lib/region-config';
import { rememberTenantRegion } from '@/lib/tenant-region-hints';

export const invitationKeys = {
    all: ['invitations'] as const,
    list: (tenantId: string) => [...invitationKeys.all, tenantId] as const,
    public: (token: string) => [...invitationKeys.all, 'public', token] as const,
};

export function useInvitations(tenantId: string | undefined) {
    const api = useApi();

    return useQuery({
        queryKey: invitationKeys.list(tenantId!),
        queryFn: () => api.invitations.getTenantInvitations(tenantId!),
        enabled: !!tenantId,
    });
}

export function useCreateInvitation(tenantId: string) {
    const api = useApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: ICreateInvitation) => api.invitations.createInvitation(tenantId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: invitationKeys.list(tenantId) });
        },
    });
}

export function useRevokeInvitation(tenantId: string) {
    const api = useApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (invitationId: string) => api.invitations.revokeInvitation(tenantId, invitationId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: invitationKeys.list(tenantId) });
        },
    });
}

export function usePublicInvitation(token: string | null, region: string | null) {
    const api = useApi();
    const baseUrl = region ? getApiUrlForRegion(region) : undefined;

    return useQuery({
        queryKey: [...invitationKeys.public(token!), region],
        queryFn: () => api.invitations.getPublicInvitation(token!, baseUrl),
        enabled: !!token && !!region,
    });
}

export function useAcceptInvitation(region: string | null) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { refreshToken } = useRegion();

    const baseUrl = region ? getApiUrlForRegion(region) : undefined;

    return useMutation({
        mutationFn: (data: IAcceptInvitation) => api.invitations.acceptInvitation(data, baseUrl),
        onSuccess: async (accepted) => {
            // The invitation named the region, so record it: the membership is
            // reachable through the registry even if the claim never catches up
            // (ESCLOUD-268).
            if (region) {
                rememberTenantRegion(accepted.tenant.id, region);
            }

            // Refresh Auth0 token to get updated tenant claims with new membership
            try {
                await refreshToken();
            } catch (error) {
                logError(
                    errorIds.INVITATION_CLAIM_REFRESH_FAILED,
                    'Failed to refresh tenant claims after accepting an invitation',
                    { tenantId: accepted.tenant.id, error }
                );
            }
            queryClient.invalidateQueries({
                queryKey: region ? userKeys.tenantsInRegion(region) : userKeys.tenants(),
            });
            queryClient.invalidateQueries({ queryKey: memberKeys.all });
        },
    });
}
